"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useServerlessWebRTC = void 0;
const react_1 = require("react");
const defaultConfig = {
    useIceServer: false,
};
function calculateConnectionState(rtcConnectionState, hasRemoteDescription) {
    if (rtcConnectionState === "new") {
        return "initial";
    }
    if (rtcConnectionState === "connecting") {
        if (!hasRemoteDescription) {
            return "waitingForRemoteDescription";
        }
        else {
            return "needToSendLocalDescription";
        }
    }
    if (rtcConnectionState === "connected") {
        return "connected";
    }
    if (rtcConnectionState === "closed" ||
        rtcConnectionState === "disconnected" ||
        rtcConnectionState === "failed") {
        return "disconnected";
    }
    return "disconnected";
}
const useServerlessWebRTC = (config) => {
    const { useIceServer } = Object.assign(Object.assign({}, defaultConfig), config);
    const [peerConnection, setPeerConnection] = (0, react_1.useState)();
    const [dataChannel, setDataChannel] = (0, react_1.useState)();
    const [rtcConnectionState, setRTCConnectionState] = (0, react_1.useState)("new");
    const [isIceGatheringComplete, setIsIceGatheringComplete] = (0, react_1.useState)(false);
    const [localDescription, setLocalDescription] = (0, react_1.useState)();
    const [hasRemoteDescription, setHasRemoteDescription] = (0, react_1.useState)(false);
    const [messageHandlers, setMessageHandlers] = (0, react_1.useState)({});
    (0, react_1.useEffect)(() => {
        let shouldContinue = true;
        let peerConnection;
        const senders = [];
        const setup = () => __awaiter(void 0, void 0, void 0, function* () {
            peerConnection = new RTCPeerConnection({
                iceServers: useIceServer
                    ? [{ urls: "stun:stun.l.google.com:19302" }]
                    : [],
            });
            const dataChannel = peerConnection.createDataChannel("text", {
                negotiated: true,
                id: 0,
            });
            setDataChannel(dataChannel);
            peerConnection.onnegotiationneeded = () => __awaiter(void 0, void 0, void 0, function* () {
                yield peerConnection.setLocalDescription();
            });
            peerConnection.onicegatheringstatechange = () => {
                const iceGatheringState = peerConnection.iceGatheringState;
                if (iceGatheringState === "new" || iceGatheringState === "gathering") {
                    setIsIceGatheringComplete(false);
                }
                else if (iceGatheringState === "complete") {
                    setIsIceGatheringComplete(true);
                    setLocalDescription(peerConnection.localDescription || undefined);
                }
            };
            peerConnection.onconnectionstatechange = () => {
                const connectionState = peerConnection.connectionState;
                setRTCConnectionState(connectionState);
            };
            shouldContinue && setPeerConnection(peerConnection);
        });
        setup();
        return () => {
            shouldContinue = false;
            for (const sender of senders) {
                peerConnection === null || peerConnection === void 0 ? void 0 : peerConnection.removeTrack(sender);
            }
            setPeerConnection(undefined);
            setDataChannel(undefined);
            setRTCConnectionState("new");
            setLocalDescription(undefined);
            setIsIceGatheringComplete(false);
            setHasRemoteDescription(false);
        };
    }, [useIceServer]);
    (0, react_1.useEffect)(() => {
        if (!dataChannel)
            return;
        dataChannel.onmessage = (event) => {
            let message;
            try {
                message = JSON.parse(event.data);
            }
            catch (error) {
                console.error("Could not parse message as JSON:", event.data);
            }
            if (!message) {
                console.error("A message was not provided.");
                return;
            }
            if (!message.type) {
                console.error("The message did not contain a type");
                return;
            }
            const messageHandler = messageHandlers[message.type];
            if (!messageHandler) {
                console.warn("No handler registered for message with type:", message.type);
                return;
            }
            messageHandler(message);
        };
    }, [dataChannel, messageHandlers]);
    const setRemoteDescription = (remoteDescriptionString) => __awaiter(void 0, void 0, void 0, function* () {
        if (!peerConnection)
            throw new Error("Tried to set remote description before Peer Connection was created.");
        const remoteDescription = JSON.parse(remoteDescriptionString);
        yield peerConnection.setRemoteDescription(remoteDescription);
        setHasRemoteDescription(true);
        if (remoteDescription.type === "offer") {
            setLocalDescription(undefined);
            yield peerConnection.setLocalDescription();
            setLocalDescription(peerConnection.localDescription || undefined);
        }
    });
    const sendMessage = (messageType, data) => {
        if (!dataChannel)
            return;
        const message = {
            type: messageType,
            data,
        };
        dataChannel.send(JSON.stringify(message));
    };
    const registerEventHandler = (0, react_1.useCallback)((messageType, handler) => {
        setMessageHandlers((prev) => (Object.assign(Object.assign({}, prev), { [messageType]: handler })));
        return () => {
            setMessageHandlers((prev) => {
                const newHandlers = Object.assign({}, prev);
                delete newHandlers[messageType];
                return newHandlers;
            });
        };
    }, []);
    return {
        localDescription: JSON.stringify(localDescription),
        isLocalDescriptionReady: !isIceGatheringComplete,
        setRemoteDescription,
        sendMessage,
        registerEventHandler,
        connectionState: calculateConnectionState(rtcConnectionState, hasRemoteDescription),
    };
};
exports.useServerlessWebRTC = useServerlessWebRTC;
//# sourceMappingURL=index.js.map